/**
 * Marketing-optimized AppTheme — provides the warm-success theme without firebase.
 *
 * This is a lightweight alternative to AppTheme that:
 * - Uses a static theme variant ("warm-success") instead of Firestore config
 * - Skips custom theme loading (getCustomThemeById → firebase)
 * - Skips useGlobalTheme (useSectionAccess → firebase)
 *
 * Reuses the same theme primitives and MUI customizations as the full AppTheme,
 * so marketing pages render identically to the authenticated app.
 */
import * as React from 'react';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import { inputsCustomizations } from './customizations/inputs';
import { dataDisplayCustomizations } from './customizations/dataDisplay';
import { feedbackCustomizations } from './customizations/feedback';
import { navigationCustomizations } from './customizations/navigation';
import { surfacesCustomizations } from './customizations/surfaces';
import { colorSchemesWarmSuccess, typography, shadows } from './themePrimitives';

interface AppThemeMarketingProps {
  children: React.ReactNode;
}

const warmSuccessTheme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-mui-color-scheme',
    cssVarPrefix: 'template',
  },
  colorSchemes: colorSchemesWarmSuccess,
  typography,
  shadows,
  shape: { borderRadius: 8 },
  components: {
    ...inputsCustomizations,
    ...dataDisplayCustomizations,
    ...feedbackCustomizations,
    ...navigationCustomizations,
    ...surfacesCustomizations,
    MuiCssBaseline: {
      styleOverrides: {
        '::selection': ({ theme }: { theme: any }) => ({
          backgroundColor: alpha(theme.palette.primary.main, 0.15),
          color: theme.palette.text.primary,
        }),
        ':root': {
          '--fitd-bg-primary': '#FFFBF5',
          '--fitd-bg-card': '#FFFFFF',
          '--fitd-bg-elevated': '#FFFAF3',
          '--fitd-border': '#E7E5E4',
          '--fitd-button-primary': '#166534',
          '--fitd-button-hover': '#16A34A',
          '--fitd-button-text': '#FFFFFF',
          '--fitd-text-primary': '#1C1917',
          '--fitd-text-secondary': '#57534E',
          '--fitd-text-muted': '#78716C',
          '--fitd-accent': '#EA580C',
          '--fitd-success': '#22C55E',
          '--fitd-warning': '#F59E0B',
          '--fitd-shadow': '0 4px 6px -1px rgba(28, 25, 23, 0.07), 0 2px 4px -2px rgba(28, 25, 23, 0.05)',
          '--fitd-shadow-hover': '0 10px 15px -3px rgba(28, 25, 23, 0.08), 0 4px 6px -4px rgba(28, 25, 23, 0.05)',
          '--fitd-shadow-primary': '0 8px 24px rgba(22, 101, 52, 0.25)',
          '--fitd-focus-ring': '0 0 0 3px rgba(34, 197, 94, 0.4)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          textTransform: 'none' as const,
          fontWeight: 600,
          borderRadius: '8px',
          padding: '12px 24px',
          boxShadow: 'none',
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { transform: 'translateY(-2px)' },
          '&:active': { transform: 'translateY(0)' },
          '&.MuiButton-contained': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: 'var(--fitd-shadow-primary)',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.9),
              boxShadow: '0 12px 32px rgba(22, 101, 52, 0.35)',
            },
          },
          '&.MuiButton-outlined': {
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          },
          '&.MuiButton-text': {
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          borderRadius: '12px',
          border: '1px solid',
          borderColor: theme.palette.divider,
          boxShadow: 'none',
          padding: '24px',
          transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 'var(--fitd-shadow-hover)',
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          borderRadius: '9999px',
          fontWeight: 500,
          fontSize: '0.875rem',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
        }),
        outlined: ({ theme }: { theme: any }) => ({
          borderColor: theme.palette.divider,
          color: theme.palette.text.secondary,
          backgroundColor: 'transparent',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: '12px',
        },
        elevation1: {
          boxShadow: 'var(--fitd-shadow)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': { borderColor: theme.palette.divider },
            '&:hover fieldset': { borderColor: alpha(theme.palette.text.primary, 0.3) },
            '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
          },
          '& .MuiInputBase-input': {
            '&::placeholder': { color: theme.palette.text.secondary, opacity: 0.7 },
          },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          borderRadius: '12px !important',
          border: '1px solid',
          borderColor: theme.palette.divider,
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0, boxShadow: 'var(--fitd-shadow)' },
        }),
      },
    },
  },
});

export default function AppThemeMarketing({ children }: AppThemeMarketingProps) {
  return (
    <ThemeProvider theme={warmSuccessTheme} defaultMode="light" disableTransitionOnChange>
      {children}
    </ThemeProvider>
  );
}
